import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { FileMetadata } from '../../../models/file-metadata.model';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrchestratorService } from '../../../services/orchestrator/orchestrator.service';
import { FileService } from '../../../services/file/file.service';
import { Subscription } from 'rxjs';
import { ERROR_SNACKBAR_CONFIG } from '../../../constants';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

@Component({
  selector: 'app-update-model-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInput,
  ],
  templateUrl: './update-model-dialog.component.html',
  styleUrls: ['./update-model-dialog.component.scss'],
})
export class UpdateModelDialogComponent implements OnDestroy {
  model: FileMetadata;
  form: FormGroup;
  fileError = '';
  loading = false;

  private _subs: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) dialogData: FileMetadata,
    private readonly _dialogRef: MatDialogRef<UpdateModelDialogComponent>,
    private readonly _orchestrator: OrchestratorService,
    private readonly _fileService: FileService,
    private readonly _snackBar: MatSnackBar
  ) {
    this.model = dialogData;

    this.form = new FormGroup({
      file: new FormControl<File | null>(null, [
        Validators.required,
        this._excelFileValidator.bind(this),
      ]),
      modelVersion: new FormControl<string>(this.model?.modelVersion ?? '', [
        Validators.required,
        // optional: allow letters/numbers/dot/dash/underscore/spaces
        Validators.pattern(/^[\w.\- ]+$/),
      ]),
    });
  }

  ngOnDestroy(): void {
    this._subs.forEach((s) => s.unsubscribe());
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    this.form.get('file')?.setValue(file);
    this.form.get('file')?.markAsTouched();
    this.form.get('file')?.updateValueAndValidity();

    // Run the same compliance check used in the creation form
    const sub = this._orchestrator.runComplianceCheckOnFile(file).subscribe({
      next: () => {
        this.fileError = '';
      },
      error: (err) => {
        this.fileError =
          err?.message ?? 'Erreur inconnue lors de la vérification.';
      },
    });
    this._subs.push(sub);
  }

  onSubmit(): void {
    if (this.form.invalid || this.fileError) return;

    const file = this.form.get('file')!.value as File;
    const modelVersion = this.form.get('modelVersion')!.value as string;

    this.loading = true;

    const sub = this._fileService
      .modifyExcelFile(this.model.id, modelVersion, file)
      .subscribe({
        next: () => {
          this.loading = false;
          this._snackBar.open('Modèle mis à jour avec succès !');
          this._dialogRef.close({ updated: true, id: this.model.id });
        },
        error: () => {
          this.loading = false;
          this._snackBar.open(
            'Erreur lors de la mise à jour du modèle. Si le problème persiste, contactez le support.',
            'Fermer',
            ERROR_SNACKBAR_CONFIG
          );
        },
      });

    this._subs.push(sub);
  }

  close(): void {
    this._dialogRef.close();
  }

  private _excelFileValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const file = control.value;
    if (!file) return { required: true };

    const allowedTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
    ];

    return file instanceof File && allowedTypes.includes(file.type)
      ? null
      : { invalidFileType: true };
  }
}
